import { z } from "zod";

// Client-side env (safe to expose)
const clientEnvSchema = z.object({
  NEXT_PUBLIC_THJ_API_URL: z.string().url(),
  NEXT_PUBLIC_DYNAMIC_ENVIRONMENT_ID: z.string().min(1),
  NEXT_PUBLIC_DYNAMIC_COOKIE_DOMAIN: z.string().optional(), // e.g., "auth.0xhoneyjar.xyz"
  NEXT_PUBLIC_MAINNET_RPC_URL: z.string().url(),
  NEXT_PUBLIC_BERA_RPC_URL: z.string().url(),
});

// Server-only env (never exposed to browser)
const serverEnvSchema = z.object({
  ADMIN_PASSWORD: z.string().optional(),
  ALLOWED_PARENT_URL: z.string().url().optional(),
  UPSTASH_REDIS_REST_URL: z.string().url().optional(),
  UPSTASH_REDIS_REST_TOKEN: z.string().optional(),
  // Dynamic Labs Server API Token (for minified JWT support)
  DYNAMIC_AUTH_TOKEN: z.string().optional(),
  // Optional service keys used by API routes
  HAIKU_API_KEY: z.string().optional(),
  DUNE_API_KEY: z.string().optional(),
  DATAFAST_API_KEY: z.string().optional(),
  NODE_ENV: z.enum(["development", "test", "production"]).default("development"),
});

// Parse client env eagerly — this module is imported by client code
const _client = clientEnvSchema.safeParse({
  NEXT_PUBLIC_THJ_API_URL: process.env.NEXT_PUBLIC_THJ_API_URL ?? "http://localhost:3002",
  NEXT_PUBLIC_DYNAMIC_ENVIRONMENT_ID: process.env.NEXT_PUBLIC_DYNAMIC_ENVIRONMENT_ID,
  NEXT_PUBLIC_DYNAMIC_COOKIE_DOMAIN: process.env.NEXT_PUBLIC_DYNAMIC_COOKIE_DOMAIN,
  NEXT_PUBLIC_MAINNET_RPC_URL: process.env.NEXT_PUBLIC_MAINNET_RPC_URL,
  NEXT_PUBLIC_BERA_RPC_URL: process.env.NEXT_PUBLIC_BERA_RPC_URL,
});

if (!_client.success) {
  throw new Error("Missing or invalid NEXT_PUBLIC_* env vars");
}

export const clientEnv = _client.data;
// Convenience alias for client consumers
export const env = clientEnv;

// Parse server env lazily and only on the server
let _server: z.infer<typeof serverEnvSchema> | undefined;
if (typeof window === "undefined") {
  const parsed = serverEnvSchema.safeParse({
    ADMIN_PASSWORD: process.env.ADMIN_PASSWORD,
    ALLOWED_PARENT_URL: process.env.ALLOWED_PARENT_URL,
    UPSTASH_REDIS_REST_URL: process.env.UPSTASH_REDIS_REST_URL,
    UPSTASH_REDIS_REST_TOKEN: process.env.UPSTASH_REDIS_REST_TOKEN,
    DYNAMIC_AUTH_TOKEN: process.env.DYNAMIC_AUTH_TOKEN,
    HAIKU_API_KEY: process.env.HAIKU_API_KEY,
    DUNE_API_KEY: process.env.DUNE_API_KEY,
    DATAFAST_API_KEY: process.env.DATAFAST_API_KEY,
    NODE_ENV: process.env.NODE_ENV,
  });
  if (!parsed.success) {
    throw new Error("Missing or invalid server env vars");
  }
  _server = parsed.data;
}

export const serverEnv = _server as z.infer<typeof serverEnvSchema>;

export type ClientEnv = z.infer<typeof clientEnvSchema>;
export type ServerEnv = z.infer<typeof serverEnvSchema>;
